// The capacity view (PRD §3.9, CM1, CM3–CM5, CM7): each person's load by week, month or quarter
// across every counted project, colored by §3.9 with the percentage written in, so it reads without
// color. Status toggles, probability weighting and filters recompute in the browser.

import { useCodeLabels } from "../codes";
import { useMemo, useState } from "react";
import { Body1, Caption1, Card, Checkbox, Dropdown, Input, Link, Option, Select, Subtitle2, Switch, Tab, TabList } from "@fluentui/react-components";
import { addDays, capacityView, DEFAULT_LOAD_STATUSES, mondayOf, statusOf, STATUS_LABEL, today, type CapacityCell, type ProjectStatus, type Zoom } from "../../../core";
import { useApi, type CapacityResponse } from "../api";
import { DateInput } from "../DateInput";
import { day, pct, money } from "../format";
import { href } from "../router";
import { ErrorBar, Kpi, Loading, Page, useMe, ArchivedSwitch, useShowArchived } from "../ui";

const OPEN: ProjectStatus[] = ["prospect", "opportunity", "pending", "booked"];
const num1 = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });

type Filter = "people" | "projects" | "clients" | "roles";


export function Capacity() {
  const { label: codeLabel } = useCodeLabels();
  const me = useMe();
  const [zoom, setZoom] = useState<Zoom>("week");
  const [start, setStart] = useState(mondayOf(today()));
  const [weeks, setWeeks] = useState(26);
  const [statuses, setStatuses] = useState<Set<ProjectStatus>>(new Set(DEFAULT_LOAD_STATUSES));
  const [closed, setClosed] = useState(false);
  const [weighted, setWeighted] = useState(false);
  const [billableOnly, setBillableOnly] = useState(false);
  const [filters, setFilters] = useState<Record<Filter, string[]>>({ people: [], projects: [], clients: [], roles: [] });
  // Active people by default; archived ones join with the switch (Chris, 2026-10-14 and 2026-10-15).
  const [showArchived, setShowArchived] = useShowArchived("capacity");
  const [show, setShow] = useState<"hours" | "percent" | "bench">("hours");
  const data = useApi<CapacityResponse>(`/capacity?start=${start}&weeks=${weeks}`);
  const d = data.data;

  const asSet = (xs: string[]) => (xs.length ? new Set(xs) : undefined);
  const view = useMemo(
    () =>
      d &&
      capacityView(d, zoom, {
        statuses,
        closed,
        weighted,
        billableOnly,
        people: filters.people.length ? asSet(filters.people) : showArchived ? undefined : new Set((d.people ?? []).filter((p) => !p.archived).map((p) => p.id)),
        projects: asSet(filters.projects),
        clients: asSet(filters.clients),
        roles: asSet(filters.roles),
      }),
    [d, zoom, statuses, closed, weighted, billableOnly, filters, showArchived],
  );

  const toggle = (s: ProjectStatus, on: boolean) => {
    const next = new Set(statuses);
    if (on) next.add(s);
    else next.delete(s);
    setStatuses(next);
  };
  const thresholds = d?.thresholds ?? { over: 1.1, under: 0.9 };
  // CM6: capacity nobody has planned work for.
  const benchOf = (c: CapacityCell) => Math.max(0, c.capacity - c.hours);
  const rates = (d as { rates?: Record<string, number> } | undefined)?.rates;
  const projectName = new Map((d?.projects ?? []).map((p) => [p.id, p.code ? `${codeLabel(p.code)} — ${p.name}` : p.name]));
  const roleName = new Map((d?.roles ?? []).map((r) => [r.id, r.name]));
  const options: Record<Filter, { id: string; text: string }[]> = {
    people: (d?.people ?? []).filter((p) => showArchived || !p.archived).map((p) => ({ id: p.id, text: p.archived ? `${p.name} (archived)` : p.name })),
    projects: (d?.projects ?? []).filter((p) => p.code).map((p) => ({ id: p.id, text: `${codeLabel(p.code)} — ${p.name}` })).sort((a, b) => a.text.localeCompare(b.text)),
    clients: [...new Set((d?.projects ?? []).map((p) => p.clientName).filter(Boolean))].sort().map((c) => ({ id: c, text: c })),
    roles: (d?.roles ?? []).map((r) => ({ id: r.id, text: r.name })),
  };
  const filtered = filters.people.length + filters.projects.length + filters.clients.length + filters.roles.length > 0;
  const over = view?.people.filter((r) => r.cells.some((c) => c.load !== null && c.load > thresholds.over)).length ?? 0;
  const under = view?.people.filter((r) => r.total.load !== null && r.total.load < thresholds.under).length ?? 0;

  return (
    <Page title="Capacity">
      <div className="toolbar">
        <TabList size="small" selectedValue={zoom} onTabSelect={(_, x) => setZoom(x.value as Zoom)}>
          <Tab value="week">Weeks</Tab>
          <Tab value="month">Months</Tab>
          <Tab value="quarter">Quarters</Tab>
        </TabList>
        <span className="divider" />
        <label className="inline-field">
          <Caption1>From</Caption1>
          <DateInput size="small" type="date" value={start} onChange={(_, x) => x.value && setStart(mondayOf(x.value))} aria-label="From" />
        </label>
        <label className="inline-field">
          <Caption1>For</Caption1>
          <Select size="small" value={String(weeks)} onChange={(_, x) => setWeeks(Number(x.value))} aria-label="How many weeks">
            <option value="13">13 weeks</option>
            <option value="26">26 weeks</option>
            <option value="52">52 weeks</option>
          </Select>
        </label>
        <Caption1 className="muted">to {day(addDays(start, weeks * 7 - 1))}</Caption1>
      </div>
      <div className="toolbar">
        <Caption1>Count</Caption1>
        {OPEN.map((s) => (
          <Checkbox key={s} label={STATUS_LABEL[s]} checked={statuses.has(s)} onChange={(_, x) => toggle(s, !!x.checked)} />
        ))}
        <Checkbox label="Closed (history)" checked={closed} onChange={(_, x) => setClosed(!!x.checked)} />
        <span className="divider" />
        <Switch label="Weight prospects and opportunities by probability" checked={weighted} disabled={!statuses.has("prospect") && !statuses.has("opportunity")} onChange={(_, x) => setWeighted(x.checked)} />
        <Switch label="Billable only" checked={billableOnly} onChange={(_, x) => setBillableOnly(x.checked)} />
      </div>
      <div className="toolbar">
        <Dropdown
          size="small"
          multiselect
          className="filter"
          aria-label="Filter people"
          placeholder={showArchived ? "All people" : "All active people"}
          selectedOptions={filters.people}
          value={options.people.filter((o) => filters.people.includes(o.id)).map((o) => o.text).join(", ")}
          onOptionSelect={(_, x) => setFilters((all) => ({ ...all, people: x.selectedOptions }))}
        >
          {options.people.map((o) => (
            <Option key={o.id} value={o.id} text={o.text}>
              {o.text}
            </Option>
          ))}
        </Dropdown>
        <ArchivedSwitch
          count={(d?.people ?? []).filter((p) => p.archived).length}
          on={showArchived}
          onChange={(on) => {
            setShowArchived(on);
            if (!on) setFilters((all) => ({ ...all, people: all.people.filter((id) => !d?.people.find((p) => p.id === id)?.archived) }));
          }}
        />
        {(["projects", "clients", "roles"] as const).map((f) => (
          <Dropdown
            key={f}
            size="small"
            multiselect
            className="filter"
            placeholder={`All ${f}`}
            aria-label={`Filter ${f}`}
            selectedOptions={filters[f]}
            value={options[f].filter((o) => filters[f].includes(o.id)).map((o) => o.text).join(", ")}
            onOptionSelect={(_, x) => setFilters((all) => ({ ...all, [f]: x.selectedOptions }))}
          >
            {options[f].map((o) => (
              <Option key={o.id} value={o.id} text={o.text}>
                {o.text}
              </Option>
            ))}
          </Dropdown>
        ))}
        {filtered && (
          <Link
            as="button"
            onClick={() => setFilters({ people: [], projects: [], clients: [], roles: [] })}
          >
            Clear filters
          </Link>
        )}
      </div>
      <ErrorBar message={data.error} />
      {!view ? (
        data.loading && <Loading />
      ) : (
        <>
          <div className="kpis" aria-label="Capacity summary">
            <Kpi label="Billable utilization" value={view.billableUtilization === null ? "—" : pct(view.billableUtilization)} sub="Billable hours ÷ capacity, whole window" />
            <Kpi label="Average load" value={view.averageLoad === null ? "—" : pct(view.averageLoad)} sub="All counted hours ÷ capacity" />
            <Kpi label="Over-allocated" value={`${over} ${over === 1 ? "person" : "people"}`} sub={`Above ${pct(thresholds.over, 0)} in at least one ${zoom}`} />
            <Kpi label="Under-allocated" value={`${under} ${under === 1 ? "person" : "people"}`} sub={`Below ${pct(thresholds.under, 0)} across the window`} />
            <Kpi
              label="Bench"
              value={`${num1.format(view.people.reduce((t, r) => t + benchOf(r.total), 0))} h`}
              sub={rates ? `${money(view.people.reduce((t, r) => t + benchOf(r.total) * (rates[r.personId] ?? 0), 0))} of capacity with no planned work` : "capacity with no planned work"}
            />
          </div>
          <Card className="panel wide">
            <div className="toolbar">
              <Subtitle2>Load by person</Subtitle2>
              <TabList size="small" selectedValue={show} onTabSelect={(_, x) => setShow(x.value as "hours" | "percent" | "bench")} aria-label="Show load as">
                <Tab value="hours">Hours</Tab>
                <Tab value="percent">% of capacity</Tab>
                <Tab value="bench">Bench</Tab>
              </TabList>
            </div>
            <Caption1 className="muted">
              {show === "bench"
                ? "Bench: capacity nobody has planned work for (capacity less planned hours), and what those hours cost at each person's rate."
                : show === "hours"
                  ? "Planned hours in each period."
                  : "Planned hours ÷ capacity, net of holidays."}{" "}
              Red over {pct(thresholds.over, 0)}, teal on target, blue under {pct(thresholds.under, 0)}. A
              cancelled project's weeks are released from its cancellation date. Hover a cell for what makes it up.
            </Caption1>
            {view.people.length === 0 ? (
              <Body1 className="muted">No one matches.</Body1>
            ) : (
              <div className="table-scroll">
                <table className="load-table" aria-label="Load by person and period">
                  <thead>
                    <tr>
                      <th>Person</th>
                      {view.periods.map((p) => (
                        <th key={p.key} className="num">
                          {p.label}
                        </th>
                      ))}
                      <th className="num">{show === "bench" ? "Bench h" : show === "hours" ? "Billable h" : "Billable"}</th>
                      <th className="num">{show === "bench" ? (rates ? "Bench cost" : "") : show === "hours" ? "Total h" : "Average"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.people.map((r) => (
                      <tr key={r.personId}>
                        <th scope="row">{me.can("comp.read") ? <a href={href("people", r.personId)}>{r.name}</a> : r.name}</th>
                        {r.cells.map((c, i) => (
                          <LoadCell key={view.periods[i].key} cell={c} weeks={view.periods[i].weeks.length} thresholds={thresholds} title={`${r.name}, ${view.periods[i].label}`} projectName={projectName} show={show} />
                        ))}
                        {show === "bench" ? (
                          <>
                            <td className="num">{num1.format(benchOf(r.total))}</td>
                            <td className="num">{rates ? money(benchOf(r.total) * (rates[r.personId] ?? 0)) : ""}</td>
                          </>
                        ) : (
                          <>
                            <td className="num">{show === "hours" ? num1.format(r.total.billable) : r.total.capacity > 0 ? pct(r.total.billable / r.total.capacity, 0) : "—"}</td>
                            <td className="num">{show === "hours" ? num1.format(r.total.hours) : r.total.load === null ? "—" : r.total.load === Infinity ? "no cap." : pct(r.total.load, 0)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          {view.unstaffed.length > 0 && (
            <Card className="panel wide">
              <Subtitle2>Unstaffed: placeholder hours by role</Subtitle2>
              <Caption1 className="muted">Hours a week planned for a role with nobody named yet.</Caption1>
              <div className="table-scroll">
                <table className="load-table" aria-label="Unstaffed hours by role">
                  <thead>
                    <tr>
                      <th>Role</th>
                      {view.periods.map((p) => (
                        <th key={p.key} className="num">
                          {p.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {view.unstaffed.map((u) => (
                      <tr key={u.roleId}>
                        <th scope="row">{roleName.get(u.roleId) ?? u.roleId}</th>
                        {view.periods.map((p) => {
                          const h = p.weeks.reduce((s, w) => s + u.hours[w], 0) / p.weeks.length;
                          return (
                            <td key={p.key} className="num">
                              {h ? num1.format(h) : ""}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </Page>
  );
}

function LoadCell({ cell, weeks, thresholds, title, projectName, show }: { cell: CapacityCell; weeks: number; thresholds: { over: number; under: number }; title: string; projectName: Map<string, string>; show: "hours" | "percent" | "bench" }) {
  if (show === "bench") {
    const bench = Math.max(0, cell.capacity - cell.hours);
    return <td className="num" title={`${title}: ${num1.format(bench)} h of capacity with no planned work`}>{bench ? num1.format(bench) : ""}</td>;
  }
  if (cell.load === null || (cell.load === 0 && !cell.hours)) return <td className="num" />;
  const perWeek = (h: number) => `${num1.format(h / weeks)} h/wk`;
  const tip = [
    cell.load === Infinity ? `${title}: no capacity on file` : `${title}: ${pct(cell.load, 0)} of ${perWeek(cell.capacity)}`,
    ...cell.contributors.map((c) => `${projectName.get(c.projectId) ?? "Another project"}: ${perWeek(c.hours)}`),
  ].join("\n");
  const cls = cell.load === Infinity ? "load-over" : `load-${statusOf(cell.load, thresholds)}`;
  return (
    <td className={`num ${cls}`} title={tip}>
      {show === "hours" ? num1.format(cell.hours) : cell.load === Infinity ? "no cap." : cell.load > 9.99 ? ">999%" : pct(cell.load, 0)}
    </td>
  );
}
